import { useEffect } from 'react'
import { ReactNode } from 'react'
import { Button } from '@systemeio/components'
import { Download01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import CertificateCheckIcon from 'shared/icons/certificate-check-icon'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { useDownloadCertificate } from 'modules/course/api/download-certificate'
import CourseLayout from 'modules/course/components/course-layout'
import useCertificateOfCompletion from 'modules/course/hooks/use-certificate-of-completion'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import useStepRouter from 'modules/course/hooks/use-step-router'
import { getCoursePageLayout } from 'modules/course/layouts/get-course-page-layout'

export default function CertificateOfCompletion(): ReactNode {
  const { t } = useTranslation()
  const { getCertificateLink, isCertificateAvailable } = useCertificateOfCompletion()
  const { courseData } = useCourseData()
  const { findStep, redirectToStep } = useStepRouter()

  const downloadCertificateMutation = useDownloadCertificate(
    `${courseData?.name || 'Certificate'}.pdf`,
  )

  useEffect(() => {
    if (!isCertificateAvailable) {
      redirectToStep(findStep())
    }
  }, [isCertificateAvailable, findStep])

  const handleDownload = (): void => {
    downloadCertificateMutation.mutate(getCertificateLink())
  }

  return (
    <CourseLayout>
      <div
        data-testid="course-certificate"
        className="flex h-fit w-full items-center justify-center rounded-lg bg-THEME-background p-8"
      >
        <div className="flex w-full flex-col items-center justify-center gap-11 rounded-xl border-2 border-dashed border-THEME-disabled bg-THEME-block-background p-10 text-center">
          <CertificateCheckIcon
            data-testid="course-certificate-success-icon"
            className="text-THEME-primary"
          />
          <h2 className="text-THEME-primary">{t('course.certificate.page.heading')}</h2>
          <p
            className="max-w-xl text-lg text-THEME-primary"
            dangerouslySetInnerHTML={{
              __html: t('course.certificate.page.description', { courseName: courseData?.name }),
            }}
          ></p>
          <Button
            className="h-auto max-w-full bg-THEME-secondary! font-bold whitespace-normal text-THEME-block-background! hover:bg-THEME-hover-secondary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:bg-THEME-disabled!"
            onClick={handleDownload}
            isLoading={downloadCertificateMutation.isPending}
            disabled={!isCertificateAvailable}
          >
            <Download01Icon className="text-THEME-block-background" />
            {t('course.certificate.cta')}
          </Button>
        </div>
      </div>
    </CourseLayout>
  )
}

CertificateOfCompletion.getLayout = getCoursePageLayout

export const getServerSideProps = withDomainAssets(baseServerSideProps)
